import { useCallback, useEffect, useRef } from "react";

import { useWalletStore } from "../../stores/walletStore";
import type { TokenBalanceDelta, TokenBalances } from "../types/balances";
import {
    DEFAULT_STALE_BALANCE_MS,
    delay,
    getDisplayBalances,
    isBalanceDataStale,
    reconcileBalancesAfterSwap,
    type ReconcileBalancesOptions
} from "../utils/balanceReconciliation";

import { useWallet } from "./useWallet";

export type { ReconcileBalancesOptions } from "../utils/balanceReconciliation";

export interface UseTokenBalancesDataResult {
    balances: TokenBalances;
    isLoading: boolean;
    error: string | null;
    isReconciling: boolean;
    lastFetchedAt: number | null;
    lastMutationAt: number | null;
}

export interface UseTokenBalancesActionsResult {
    refetch: () => void;
    refetchWithRetry: () => Promise<void>;
    refreshIfStale: (maxAgeMs?: number) => Promise<void>;
    reconcileWithOptimisticDelta: (delta: TokenBalanceDelta, options?: ReconcileBalancesOptions) => Promise<void>;
}

export type UseTokenBalancesControllerResult = UseTokenBalancesActionsResult;

function useTokenBalancesDataState(): UseTokenBalancesDataResult {
    const {
        balances: storedBalances,
        balanceOverride,
        isLoadingBalances,
        isReconcilingBalances,
        balanceError,
        lastFetchedAt,
        lastMutationAt
    } = useWalletStore();

    return {
        balances: getDisplayBalances(storedBalances, balanceOverride),
        isLoading: isLoadingBalances,
        error: balanceError,
        isReconciling: isReconcilingBalances,
        lastFetchedAt,
        lastMutationAt
    };
}

/**
 * Controller hook for Helius-backed token balances.
 * Owns lifecycle fetching and exposes explicit refresh/reconciliation actions.
 *
 * Features:
 * - Prevents duplicate fetches (guards in walletStore)
 * - Handles React Strict Mode double-mount
 * - Detects wallet switches and forces refetch
 * - Clears stale data on wallet switch
 */
export function useTokenBalancesController(): UseTokenBalancesControllerResult {
    const { publicKey, isWalletReady } = useWallet();
    const { fetchBalances, clearBalances } = useWalletStore();

    // Track last triggered address to prevent duplicate effect executions
    const lastTriggeredRef = useRef<string | null>(null);
    // Track previous publicKey to detect wallet switches
    const prevPublicKeyRef = useRef<string | null>(null);

    useEffect(() => {
        if (isWalletReady && publicKey) {
            const address = publicKey.toString();
            const prevAddress = prevPublicKeyRef.current;

            // Detect wallet switch (address changed while still connected)
            const isWalletSwitch = prevAddress !== null && prevAddress !== address;

            // Update prev ref
            prevPublicKeyRef.current = address;

            // On wallet switch, clear old data immediately and refetch
            if (isWalletSwitch) {
                clearBalances();
                lastTriggeredRef.current = address;
                void fetchBalances(publicKey);
                return;
            }

            // Prevent duplicate triggers for same address (React Strict Mode protection)
            if (lastTriggeredRef.current === address) {
                return;
            }
            lastTriggeredRef.current = address;

            // Fetch immediately - stabilization delay is handled by isWalletReady
            void fetchBalances(publicKey);
        } else {
            lastTriggeredRef.current = null;
            prevPublicKeyRef.current = null;
            clearBalances();
        }
    }, [isWalletReady, publicKey, fetchBalances, clearBalances]);

    const refetch = useCallback((): void => {
        if (publicKey) {
            void fetchBalances(publicKey, true);
        }
    }, [publicKey, fetchBalances]);

    const refetchWithRetry = useCallback(async (): Promise<void> => {
        if (!publicKey) return;

        // Fetch immediately - may get fresh data right away
        await fetchBalances(publicKey, true);

        // Second fetch after delay for blockchain propagation
        await delay(1000);
        await fetchBalances(publicKey, true);
    }, [publicKey, fetchBalances]);

    const refreshIfStale = useCallback(
        async (maxAgeMs = DEFAULT_STALE_BALANCE_MS): Promise<void> => {
            if (!publicKey) return;

            const state = useWalletStore.getState();
            if (!isBalanceDataStale(state.lastFetchedAt, state.lastMutationAt, maxAgeMs)) {
                return;
            }

            await fetchBalances(publicKey, true);
        },
        [publicKey, fetchBalances]
    );

    const reconcileWithOptimisticDelta = useCallback(
        async (delta: TokenBalanceDelta, options: ReconcileBalancesOptions = {}): Promise<void> => {
            if (!publicKey) return;

            await reconcileBalancesAfterSwap(publicKey, fetchBalances, delta, options);
        },
        [publicKey, fetchBalances]
    );

    return {
        refetch,
        refetchWithRetry,
        refreshIfStale,
        reconcileWithOptimisticDelta
    };
}

/**
 * Actions-only hook for token balance mutations.
 * Use this in transaction components that need refetch/reconciliation
 * but should NOT trigger the wallet lifecycle effect.
 *
 * Layout should use useTokenBalancesController() instead.
 */
export function useTokenBalancesActions(): UseTokenBalancesActionsResult {
    const { publicKey } = useWallet();
    const { fetchBalances } = useWalletStore();

    const refetch = useCallback((): void => {
        if (publicKey) {
            void fetchBalances(publicKey, true);
        }
    }, [publicKey, fetchBalances]);

    const refetchWithRetry = useCallback(async (): Promise<void> => {
        if (!publicKey) return;

        await fetchBalances(publicKey, true);

        await delay(1000);
        await fetchBalances(publicKey, true);
    }, [publicKey, fetchBalances]);

    const refreshIfStale = useCallback(
        async (maxAgeMs = DEFAULT_STALE_BALANCE_MS): Promise<void> => {
            if (!publicKey) return;

            const state = useWalletStore.getState();
            if (!isBalanceDataStale(state.lastFetchedAt, state.lastMutationAt, maxAgeMs)) {
                return;
            }

            await fetchBalances(publicKey, true);
        },
        [publicKey, fetchBalances]
    );

    const reconcileWithOptimisticDelta = useCallback(
        async (delta: TokenBalanceDelta, options: ReconcileBalancesOptions = {}): Promise<void> => {
            if (!publicKey) return;

            await reconcileBalancesAfterSwap(publicKey, fetchBalances, delta, options);
        },
        [publicKey, fetchBalances]
    );

    return {
        refetch,
        refetchWithRetry,
        refreshIfStale,
        reconcileWithOptimisticDelta
    };
}

/**
 * Read-only hook for effective token balances.
 * Use this in components that only need to display Helius-backed balance data.
 *
 * The controller hook should be mounted separately to manage lifecycle fetches.
 */
export function useTokenBalancesData(): UseTokenBalancesDataResult {
    return useTokenBalancesDataState();
}

/**
 * @deprecated Use useTokenBalancesController() instead.
 * Preserved for backwards compatibility and still includes lifecycle behavior.
 */
export function useTokenBalances(): UseTokenBalancesControllerResult {
    return useTokenBalancesController();
}
